import { parseISO } from "date-fns";
import { snapDateToResolutionLocal } from "../timeline/engine/snap.js";

const THUMBNAIL_SIZE_BY_UNIT = Object.freeze({
  minute: "xl",
  hour: "lg",
  day: "lg",
  week: "lg",
  month: "lg",
  year: "lg",
});

const ROW_HEIGHT_BY_SIZE = Object.freeze({
  xs: 52,
  sm: 64,
  md: 76,
  lg: 96,
  xl: 132,
});

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

function comparePhotoItems(a, b) {
  if (a.dateMs === b.dateMs) return a.index - b.index;
  return a.dateMs - b.dateMs;
}

export function resolveTimelineV3ThumbnailSize(preset) {
  const unit = preset?.clusterBucket?.unit || preset?.majorUnit || "day";
  return THUMBNAIL_SIZE_BY_UNIT[unit] || "md";
}

export function resolveTimelineV3ThumbnailSizeForDuration(durationMs) {
  if (durationMs <= 6 * HOUR_MS) return "xl";
  return "lg";
}

export function resolveTimelineV3RowHeight(thumbnailSize) {
  return ROW_HEIGHT_BY_SIZE[thumbnailSize] || ROW_HEIGHT_BY_SIZE.md;
}

export function buildTimelineV3Rows(files, sourceIndices = null, preset) {
  const bucket = preset?.clusterBucket || { unit: "day", step: 1, mode: "floor" };
  const hasSourceIndexMap =
    Array.isArray(sourceIndices) && sourceIndices.length === files.length;
  const rowsByBucket = new Map();

  files.forEach((file, localIndex) => {
    const dateTaken = file?.modifiedExifData?.dateTaken;
    if (!dateTaken) return;

    const date = parseISO(dateTaken);
    const dateMs = date.getTime();
    if (!Number.isFinite(dateMs)) return;

    const bucketDate = snapDateToResolutionLocal(date, bucket);
    const bucketMs = bucketDate.getTime();
    if (!Number.isFinite(bucketMs)) return;

    const index = hasSourceIndexMap ? sourceIndices[localIndex] : localIndex;
    if (!rowsByBucket.has(bucketMs)) {
      rowsByBucket.set(bucketMs, {
        id: `row-${bucketMs}`,
        bucketDate,
        bucketMs,
        photos: [],
      });
    }

    rowsByBucket.get(bucketMs).photos.push({
      file,
      index,
      date,
      dateMs,
    });
  });

  return Array.from(rowsByBucket.values())
    .map((row) => ({
      ...row,
      photos: row.photos.sort(comparePhotoItems),
    }))
    .sort((a, b) => a.bucketMs - b.bucketMs);
}

export function getVisibleTimelineV3Rows(rows, range) {
  const start = range?.start?.getTime?.() ?? Number.NEGATIVE_INFINITY;
  const end = range?.end?.getTime?.() ?? Number.POSITIVE_INFINITY;
  return rows.filter((row) => row.bucketMs >= start && row.bucketMs <= end);
}

function buildDisplayRow(group, getTimestampTopPx) {
  if (group.length === 1) {
    const row = group[0];
    return {
      ...row,
      isGroup: false,
      sourceRows: [row],
      topPx: getTimestampTopPx(row.bucketMs),
      photos: row.photos.map((photo) => ({ ...photo, timelineRow: row })),
    };
  }

  const first = group[0];
  const last = group[group.length - 1];
  const photos = group
    .flatMap((row) => row.photos.map((photo) => ({ ...photo, timelineRow: row })))
    .sort(comparePhotoItems);
  const topPx =
    group.reduce((sum, row) => sum + getTimestampTopPx(row.bucketMs), 0) / group.length;
  const bucketMs =
    group.reduce((sum, row) => sum + row.bucketMs, 0) / group.length;

  return {
    id: `group-${first.id}-${last.id}`,
    isGroup: true,
    sourceRows: group,
    bucketUnit: first.bucketUnit,
    bucketDate: first.bucketDate,
    bucketMs,
    startBucketDate: first.bucketDate,
    endBucketDate: last.bucketDate,
    startBucketMs: first.bucketMs,
    endBucketMs: last.bucketMs,
    topPx,
    photos,
  };
}

export function buildTimelineV3DisplayRows(
  rows,
  {
    getTimestampTopPx,
    minGroupGapPx = 56,
  } = {},
) {
  if (!Array.isArray(rows) || typeof getTimestampTopPx !== "function") {
    return [];
  }

  const safeMinGroupGapPx = Math.max(1, Number(minGroupGapPx) || 1);
  const orderedRows = [...rows].sort((a, b) => a.bucketMs - b.bucketMs);
  const groups = [];

  orderedRows.forEach((row) => {
    const lastGroup = groups[groups.length - 1];
    if (!lastGroup) {
      groups.push([row]);
      return;
    }

    const previousRow = lastGroup[lastGroup.length - 1];
    const gapPx = getTimestampTopPx(row.bucketMs) - getTimestampTopPx(previousRow.bucketMs);
    if (gapPx < safeMinGroupGapPx) {
      lastGroup.push(row);
      return;
    }

    groups.push([row]);
  });

  return groups.map((group) => buildDisplayRow(group, getTimestampTopPx));
}
